import { useState, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import { PlandayCompanyResponse, PlandaySetupRequest } from '@/models/payment';
import { PlandayCompanySelect } from './PlandayCompanySelect';
import { useAppDispatch } from '@/lib/hooks';
import { setLoader } from '@/lib/slices/globalLoaderSlice';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import {
    getAllIntegrationStatus,
    usePlandayCompanyInfoQuery
} from '@/services/paymentsQuery';
import { TwoInputConnection } from '@/pages/company/common/TwoInputConnection';
interface PlandayConnectProps {
    onClose?: () => void;
    onSaveSuccess: any;
}

export const PlandayConnect = (props: PlandayConnectProps) => {
    const { t } = useTranslation();
    const [plandayData, setPlandayData] = useState<PlandaySetupRequest>(
        {} as any
    );
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const [companyInfo, setCompanyInfo] = useState<PlandayCompanyResponse>();

    const { setupPlanday } = usePaymentsMutations();

    const getPlandayCompanyInfoQuery = usePlandayCompanyInfoQuery(true);
    const dispatch = useAppDispatch();

    const onContinue = () => {};

    const handleSetupDone = async () => {
        let response = await getPlandayCompanyInfoQuery.refetch();
        if (response.isSuccess) {
            setCompanyInfo(response.data.data);
        }
        if (response.isSuccess && response.data?.data?.children?.length < 1) {
            props.onSaveSuccess?.();
        }
    };

    useLayoutEffect(() => {
        if (getPlandayCompanyInfoQuery.isSuccess) {
            setCompanyInfo(getPlandayCompanyInfoQuery.data.data);
        }
    }, [getPlandayCompanyInfoQuery.isSuccess]);

    useEffect(() => {
        dispatch(setLoader(getPlandayCompanyInfoQuery.isPending));
    }, [getPlandayCompanyInfoQuery.isPending]);

    useEffect(() => {
        if (getPlandayCompanyInfoQuery.isError) {
            backendErrorHandler();
        }
    }, [getPlandayCompanyInfoQuery.isError]);
    const [isReconnect, setIsReconnect] = useState(false);

    useLayoutEffect(() => {
        if (setupPlanday.isSuccess) {
            setCompanyInfo(undefined);
            setAlert('Connected successfully', 'success');
            handleSetupDone();
            if (isReconnect) {
                setIsReconnect(false);
            }
        }
    }, [setupPlanday.isSuccess]);

    useEffect(() => {
        dispatch(setLoader(setupPlanday.isPending));

        if (setupPlanday.isError) {
            backendErrorHandler('Could not connect with Planday');
        }
    }, [setupPlanday]);

    const onClick = () => {
        dispatch(setLoader(true));
        setupPlanday.mutate({
            clientId: plandayData?.clientId,
            refreshToken: plandayData?.refreshToken
        });
    };
    const handleReconnect = () => {
        setIsReconnect(true);
    };

    return (
        <>
            {((companyInfo?.children?.length ?? 0) <= 1 || isReconnect) && (
                <>
                    <TwoInputConnection
                        input1Value={plandayData?.clientId}
                        input2Value={plandayData?.refreshToken}
                        input1Label={t('client_id')}
                        input2Label={t('refresh_token')}
                        input1Placeholder={t('client_id_placeholder')}
                        input2Placeholder={t('refresh_token_placeholder')}
                        input1OnChange={(val: any) => {
                            setPlandayData({
                                ...plandayData,
                                clientId: val.target.value
                            });
                        }}
                        input2OnChange={(val) => {
                            setPlandayData({
                                ...plandayData,
                                refreshToken: val.target.value
                            });
                        }}
                        primaryDisabled={
                            !plandayData.clientId || !plandayData?.refreshToken
                        }
                        primaryButtonTitle={t('connect')}
                        secondaryButtonTitle={t('cancel')}
                        onClickPrimary={onClick}
                        onClickSecondary={() => {
                            if (isReconnect) {
                                setIsReconnect(false);
                            } else {
                                props.onClose?.();
                            }
                        }}
                    />
                </>
            )}

            <PlandayCompanySelect
                onClose={props.onClose}
                onReconnect={handleReconnect}
                companyInfo={companyInfo}
                onSaveSuccess={props.onSaveSuccess}
                show={companyInfo?.children?.length! > 1 && !isReconnect}
            />
        </>
    );
};
